import { useCallback, useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import DEFAULT_OPERATIONS from "./Cart.gql";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { logGenerator } from "../../utility/logGenerator";
import { categoryForLayer } from "../../utility/categoryForLayer";
import { useCreateCart } from "./useCreateCart";

const UNSUPPORTED_PRODUCT_TYPES = ["virtual", "grouped", "downloadable"];

export const useAddToCartButton = (props = {}) => {
  const { addItem } = DEFAULT_OPERATIONS;
  const { item, buyNow, categoryNames } = props;
  const { addToast } = useToasts();
  const { CreateEmptyCart, CreateEmptyCartAfterSignIn } = useCreateCart();
  const { isSignedIn } = useSelector((state) => state.loginSlice);

  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);

  const isInStock = item.stock_status === "IN_STOCK";

  const productType = item.type_id;
  const isUnsupportedProductType =
    UNSUPPORTED_PRODUCT_TYPES.includes(productType);
  const isDisabled = isLoading || !isInStock || isUnsupportedProductType;

  const navigate = useNavigate();

  const { cartId } = useSelector((state) => state.cartSlice);
  const [shouldAddToCart, setShouldAddToCart] = useState(false);

  const [addToCart] = useMutation(addItem, {
    onCompleted: (data) => {
      // console.log("data", data);
      let categoryItems = "";
      if (categoryNames && categoryNames?.length) {
        categoryItems = categoryForLayer(categoryNames);
      }
      const addToCartLayer = {
        event: "add_to_cart",
        ecommerce: {
          currency: "INR",
          value: item?.price_range?.maximum_price?.final_price?.value,
          items: [
            {
              item_id: item?.sku,
              item_name: item?.name,
              affiliation: "Hettich India Online",
              discount: item?.price_range?.maximum_price?.discount?.amount_off,
              item_brand: "Hettich",
              quantity: 1,
              price: item?.price_range?.maximum_price?.final_price?.value,
              ...(categoryItems &&
                Object.keys(categoryItems).length > 0 &&
                categoryItems),
            },
          ],
        },
      };
      if (buyNow === true) {
        if (data?.addProductsToCart?.user_errors?.length >= 1) {
          addToast(data?.addProductsToCart?.user_errors[0]?.message, {
            appearance: "error",
            autoDismiss: true,
            style: {
              backgroundColor: "#DB1F00",
              color: "#fff",
            },
          });
        } else {
          // console.log("cartLayer from BuyNow", addToCartLayer);
          if (window.dataLayer) {
            window.dataLayer.push(addToCartLayer);
          }
          navigate(`/checkout`, { replace: true });
        }
      } else {
        if (data?.addProductsToCart?.user_errors?.length >= 1) {
          addToast(data?.addProductsToCart?.user_errors[0].message, {
            appearance: "error",
            autoDismiss: true,
            style: {
              backgroundColor: "#DB1F00",
              color: "#fff",
            },
          });
        } else {
          addToast("Item successfully Added to Cart", {
            appearance: "success",
            autoDismiss: true,
            style: {
              backgroundColor: "#027B51" /* Custom background color */,
              color: "#fff" /* Custom text color */,
            },
          });
          // console.log("cartLayer", addToCartLayer);
          if (window.dataLayer) {
            window.dataLayer.push(viewLayer);
          }
        }
      }
    },
    onError: async (err) => {
      if ("debugMessage" in err?.graphQLErrors[0]) {
        addToast(err?.graphQLErrors[0]?.debugMessage, {
          appearance: "warning",
          autoDismiss: true,
          style: {
            backgroundColor: "#745600",
            color: "#fff",
          },
        });
      } else {
        console.log("err.message", err.message);
        if (
          err.message.includes("Could not find a cart with ID") ||
          err.message.includes("Cart isn't active") ||
          err.message.includes("The cart isn't active.")
        ) {
          if (isSignedIn) {
            console.log("inside signed in", err.message);
            await CreateEmptyCartAfterSignIn();
          } else {
            console.log("inside not signed in", err.message);
            await CreateEmptyCart();
          }
          console.log("after create new cart");
          setShouldAddToCart(true); 
        } else if (err.message !== `Required parameter "cartId" is missing`) {
          addToast(err.message, {
            appearance: "error",
            autoDismiss: true,
            style: {
              backgroundColor: "#DB1F00",
              color: "#fff",
            },
          });
        }
      }
    },
  });

    useEffect(() => {
      if (shouldAddToCart && cartId) {
        handleAddToCart();
        setShouldAddToCart(false); // Reset
      }
    }, [cartId, shouldAddToCart]);

  const handleAddToCart = useCallback(async () => {
    try {
      if (productType === "simple") {
        if (
          item.enable_site_visit === 1 ||
          item.enable_appliance_site_visit === 1
        ) {
          navigate(`/product/${item.url_key}.html`, {
            state: { url: location.pathname },
          });
        } else {
          setIsLoading(true);

          logGenerator(
            "info",
            `useAddToCartButton handleAddToCart addToCart cartId : ${cartId}`
          );

          await addToCart({
            variables: {
              cartId,
              cartItem: {
                quantity: 1,
                entered_options: [
                  {
                    uid: item.uid,
                    value: item.name,
                  },
                ],
                sku: item.sku,
              },
            },
          });

          setIsLoading(false);
        }
      } else if (productType === "bundle") {
        navigate(`/product/${item.url_key}.html`, {
          state: { url: location.pathname },
        });
      } else if (productType === "configurable") {
        navigate(`/product/${item.url_key}.html`, {
          state: { url: location.pathname },
        });
      } else {
        console.warn("Unsupported product type unable to handle.");
      }
    } catch (error) {
      console.error(error);
    }
  }, [
    addToCart,
    cartId,
    history,
    item.sku,
    item.url_key,
    productType,
    item.uid,
    item.name,
    item.enable_site_visit,
  ]);

  return {
    handleAddToCart,
    isDisabled,
    isInStock,
  };
};
